import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Upload,
  Camera,
  Image,
  Check,
  X,
  Loader2,
  Receipt,
  DollarSign,
  FileImage,
  Smartphone,
  ChevronRight,
  ChevronDown,
  Info,
  CheckCircle,
  Copy,
  QrCode,
  Download,
  Calendar
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Device-specific screenshot instructions
const ScreenshotInstructions = ({ paymentMethod }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className="bg-stone-800/50 rounded-lg border border-stone-700">
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full p-3 flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-2">
          <Info className="w-4 h-4 text-amber-400" />
          <span className="text-sm text-gray-300">How to take a screenshot</span>
        </div>
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400" />
        )}
      </button>
      
      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-stone-700 pt-3">
          {/* iPhone Instructions */}
          <div className="bg-stone-900/50 rounded-lg p-3">
            <h5 className="text-white font-medium text-sm mb-2 flex items-center gap-2">
              <Smartphone className="w-4 h-4" />
              iPhone
            </h5>
            <ol className="text-xs text-gray-400 space-y-1 list-decimal list-inside">
              <li>Open {paymentMethod === 'cashapp' ? 'Cash App' : paymentMethod === 'zelle' ? 'your banking app' : 'PayPal'}</li>
              <li>Go to the completed payment/transaction</li>
              <li>Press <span className="text-amber-400">Side Button + Volume Up</span> together</li>
              <li>Screenshot saves to Photos</li>
            </ol>
          </div>
          
          {/* Android Instructions */}
          <div className="bg-stone-900/50 rounded-lg p-3">
            <h5 className="text-white font-medium text-sm mb-2 flex items-center gap-2">
              <Smartphone className="w-4 h-4" />
              Android
            </h5>
            <ol className="text-xs text-gray-400 space-y-1 list-decimal list-inside">
              <li>Open {paymentMethod === 'cashapp' ? 'Cash App' : paymentMethod === 'zelle' ? 'your banking app' : 'PayPal'}</li>
              <li>Go to the completed payment/transaction</li>
              <li>Press <span className="text-amber-400">Power + Volume Down</span> together</li>
              <li>Screenshot saves to Gallery</li>
            </ol>
          </div>
          
          {/* What to capture */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
            <h5 className="text-amber-400 font-medium text-sm mb-1">What to capture:</h5>
            <ul className="text-xs text-gray-300 space-y-1">
              <li>✓ Payment confirmation screen</li>
              <li>✓ Amount sent</li>
              <li>✓ Recipient name or handle</li>
              <li>✓ Date and time</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

// Payment instructions with stylist's payment handles
const PaymentInstructions = ({ stylistPaymentInfo, amount, paymentMethod, onCopyHandle }) => {
  const getPaymentHandle = () => {
    switch(paymentMethod) {
      case 'cashapp':
        return stylistPaymentInfo?.cashapp_tag || '$YourStylist';
      case 'zelle':
        return stylistPaymentInfo?.zelle_email || stylistPaymentInfo?.zelle_phone || 'stylist@email.com';
      case 'paypal':
        return stylistPaymentInfo?.paypal_email || 'stylist@paypal.com';
      default:
        return '';
    }
  };
  
  const getPaymentMethodName = () => {
    switch(paymentMethod) {
      case 'cashapp': return 'Cash App';
      case 'zelle': return 'Zelle';
      case 'paypal': return 'PayPal';
      default: return paymentMethod;
    }
  };
  
  const handle = getPaymentHandle();
  
  return (
    <div className="space-y-3">
      <div className="bg-gradient-to-r from-amber-500/20 to-yellow-500/20 rounded-xl p-4 border border-amber-500/30">
        <div className="flex items-center justify-between mb-2">
          <span className="text-gray-400 text-sm">Send to:</span>
          <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
            {getPaymentMethodName()}
          </Badge>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-white font-mono text-lg">{handle}</span>
          <Button
            size="sm"
            variant="ghost"
            className="text-amber-400 hover:text-amber-300"
            onClick={() => {
              navigator.clipboard.writeText(handle);
              toast.success('Copied to clipboard!');
              onCopyHandle?.();
            }}
          >
            <Copy className="w-4 h-4" />
          </Button>
        </div>
        <div className="mt-3 pt-3 border-t border-amber-500/30">
          <div className="flex items-center justify-between">
            <span className="text-gray-400 text-sm">Amount:</span>
            <span className="text-2xl font-bold text-amber-400">${typeof amount === 'number' && !isNaN(amount) ? amount.toFixed(2) : '0.00'}</span>
          </div>
        </div>
      </div>
      
      {/* Step by step */}
      <div className="bg-stone-800 rounded-lg p-4">
        <h4 className="text-white font-medium mb-3">Steps to Pay:</h4>
        <ol className="space-y-2 text-sm text-gray-300">
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 bg-amber-500 text-black rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
            <span>Open {getPaymentMethodName()} on your phone</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 bg-amber-500 text-black rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
            <span>Send <span className="text-amber-400 font-bold">${typeof amount === 'number' && !isNaN(amount) ? amount.toFixed(2) : '0.00'}</span> to <span className="text-amber-400 font-mono">{handle}</span></span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 bg-amber-500 text-black rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
            <span>Take a screenshot of the confirmation</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 bg-amber-500 text-black rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
            <span>Upload your screenshot below</span>
          </li>
        </ol>
      </div>
    </div>
  );
};

const PayAtServiceReceiptUpload = ({ 
  appointmentId, 
  paymentMethod, 
  amount,
  stylistPaymentInfo,
  onSuccess,
  isOpen,
  onClose
}) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [notes, setNotes] = useState("");
  const [step, setStep] = useState('instructions'); // 'instructions', 'upload', 'success'
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File too large. Maximum size is 10MB.");
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview({
          file,
          dataUrl: reader.result
        });
        setStep('upload');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!preview?.file) {
      toast.error("Please select a receipt image");
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("appointment_id", appointmentId);
      formData.append("payment_method", paymentMethod);
      formData.append("amount", amount.toString());
      formData.append("receipt_image", preview.file);
      if (notes) {
        formData.append("notes", notes);
      }

      await axios.post(
        `${API}/api/payments/receipt/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      setStep('success');
      setTimeout(() => {
        onSuccess?.();
      }, 2000);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload receipt");
    } finally {
      setUploading(false);
    }
  };

  const getPaymentMethodName = () => {
    const names = { cashapp: "Cash App", zelle: "Zelle", paypal: "PayPal" };
    return names[paymentMethod] || paymentMethod;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-stone-900 border-stone-700 max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            {step === 'success' ? (
              <>
                <CheckCircle className="w-5 h-5 text-emerald-400" />
                Payment Submitted!
              </>
            ) : (
              <>
                <DollarSign className="w-5 h-5 text-amber-400" />
                Pay with {getPaymentMethodName()}
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        {step === 'success' ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-400" />
            </div>
            <h3 className="text-white font-semibold mb-2">Receipt Uploaded Successfully!</h3>
            <p className="text-gray-400 text-sm">
              Your stylist will verify the payment shortly.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Payment Instructions */}
            <PaymentInstructions 
              stylistPaymentInfo={stylistPaymentInfo}
              amount={amount}
              paymentMethod={paymentMethod}
            />
            
            {/* Screenshot Instructions */}
            <ScreenshotInstructions paymentMethod={paymentMethod} />
            
            {/* Upload Section */}
            <div className="space-y-2">
              <Label className="text-gray-300 flex items-center gap-2">
                <Camera className="w-4 h-4" />
                Upload Payment Screenshot
              </Label>
              
              {preview ? (
                <div className="relative">
                  <img
                    src={preview.dataUrl}
                    alt="Receipt preview"
                    className="w-full rounded-lg border border-stone-700 max-h-48 object-contain bg-stone-800"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2 bg-red-500/80 hover:bg-red-600 text-white"
                    onClick={() => {
                      setPreview(null);
                      setStep('instructions');
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Option to take photo */}
                  <div
                    onClick={() => {
                      // Create temp input for camera
                      const cameraInput = document.createElement('input');
                      cameraInput.type = 'file';
                      cameraInput.accept = 'image/*';
                      cameraInput.capture = 'environment';
                      cameraInput.onchange = handleFileSelect;
                      cameraInput.click();
                    }}
                    className="border-2 border-dashed border-amber-500/50 rounded-lg p-4 text-center cursor-pointer hover:border-amber-500 transition-colors bg-amber-500/5"
                  >
                    <Camera className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                    <p className="text-amber-400 font-medium text-sm">
                      Take a photo
                    </p>
                  </div>
                  
                  {/* Option to choose from files */}
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-purple-500/50 rounded-lg p-4 text-center cursor-pointer hover:border-purple-500 transition-colors bg-purple-500/5"
                  >
                    <FileImage className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                    <p className="text-purple-400 font-medium text-sm">
                      Choose from gallery
                    </p>
                    <p className="text-gray-500 text-xs mt-1">
                      PNG, JPG up to 10MB
                    </p>
                  </div>
                </div>
              )}
              
              {/* Hidden file input for gallery selection (no capture attribute) */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>

            {/* Add to Calendar Button - Pro Feature */}
            <div className="p-4 bg-gradient-to-r from-stone-800/50 to-amber-900/20 rounded-lg border border-amber-500/30">
              <div className="flex items-center gap-3 mb-2">
                <Calendar className="w-5 h-5 text-amber-400" />
                <span className="text-white font-medium">Save to Your Calendar</span>
                <span className="px-2 py-0.5 text-xs bg-amber-500 text-black rounded-full font-medium">PRO</span>
              </div>
              <p className="text-stone-400 text-sm mb-3">Download to add this appointment to Google Calendar, Outlook, or Apple Calendar</p>
              <button 
                onClick={async () => {
                  try {
                    const token = localStorage.getItem("token");
                    const response = await fetch(`${API}/api/appointments/${appointmentId}/calendar`, {
                      headers: {
                        Authorization: `Bearer ${token}`
                      }
                    });
                    
                    if (!response.ok) {
                      throw new Error('Failed to download calendar');
                    }
                    
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `appointment_${appointmentId}.ics`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                    toast.success('Calendar event downloaded!');
                  } catch (error) {
                    console.error('Calendar download error:', error);
                    toast.error('Failed to download calendar event');
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black rounded-lg transition-colors text-sm font-medium"
              >
                <Download className="w-4 h-4" />
                Add to Calendar
              </button>
            </div>

            {/* Notes */}
            {preview && (
              <div className="space-y-2">
                <Label className="text-gray-300">Notes (Optional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional details..."
                  className="bg-stone-800 border-stone-700 text-white"
                  rows={2}
                />
              </div>
            )}

            {/* Action Button */}
            {preview && (
              <Button
                className="w-full bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-semibold"
                onClick={handleUpload}
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Submit Payment Receipt
                  </>
                )}
              </Button>
            )}

            <p className="text-xs text-gray-500 text-center">
              Your appointment will be confirmed once payment is verified.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PayAtServiceReceiptUpload;
export { PaymentInstructions, ScreenshotInstructions };
